import React, { useEffect, useRef, useState } from "react";
import {
  bannerTablePreviewBoxStyle,
  bannerTablePreviewImageStyle,
} from "./bannerImageUtils";

type Props = {
  src: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
};

const LazyBannerImage: React.FC<Props> = ({ src, alt, className, style }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !src) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "120px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [src]);

  if (!src) {
    return (
      <span style={{ color: "gray" }} className="text-sm">
        No banner available
      </span>
    );
  }

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ ...bannerTablePreviewBoxStyle, ...style }}
    >
      {failed ? (
        <div
          className="flex items-center justify-center text-sm text-gray-500 bg-gray-100 rounded"
          style={bannerTablePreviewBoxStyle}
        >
          Unable to load banner
        </div>
      ) : visible ? (
        <img
          src={src}
          alt={alt}
          style={bannerTablePreviewImageStyle}
          loading="lazy"
          onError={() => setFailed(true)}
        />
      ) : (
        <div
          style={{
            ...bannerTablePreviewBoxStyle,
            background: "#f3f4f6",
            borderRadius: "4px",
          }}
          aria-hidden
        />
      )}
    </div>
  );
};

export default LazyBannerImage;
